import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Mail, Building, Lock, Coins, Shield } from 'lucide-react';

const DataMonetizationPolicyPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Aura Shield Data Monetization Policy - SELF Labs</title>
        <meta name="description" content="Official Data Monetization Policy for Aura Shield, effective April 1, 2025. Learn about secure data monetization, ESSENCE rewards, and user sovereignty." />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 text-indigo-400" />
              <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
                Aura Shield Data Monetization Policy
              </h1>
            </div>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Introduction</h2>
              <div className="prose prose-invert max-w-none">
                <p>At SELF Labs Inc. ("SELF Labs," "we," "us," or "our"), we believe users should maintain control over their personal data — and have the option to monetize it securely when they choose.</p>
                <p>The Aura Shield™ System embedded within every SELF Phone provides a sovereign, encrypted data monetization pathway that empowers users to earn ESSENCE tokens without compromising their privacy or control.</p>
                <div className="bg-indigo-900/20 border border-indigo-400/20 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-indigo-300">Please Note:</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>The Aura Shield Data Monetization feature will officially launch during Phase 2 of the Signal Economy rollout.</li>
                    <li>Until Phase 2 activation, data monetization controls will be visible but disabled within your SELF Phone dashboard.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Voluntary Participation */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Voluntary Participation</h2>
              <div className="prose prose-invert max-w-none">
                <p>Participation in Aura Shield Data Monetization is:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>100% voluntary</li>
                  <li>Opt-in only through your SELF Phone settings</li>
                  <li>Customizable by data category (users choose what data to lock, share, or monetize)</li>
                </ul>
                <p className="mt-4">You are never required to sell your data to use the SELF Phone, Role Model AI App Store, Harmony Mesh, or participate in the Signal Economy.</p>
              </div>
            </section>

            {/* Data Types */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Data Types Eligible for Monetization</h2>
              <div className="prose prose-invert max-w-none">
                <p>Only anonymized, encrypted metadata streams will be eligible for monetization during Phase 2 activation.</p>
                <p>Examples include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Signal execution logs (not content)</li>
                  <li>Device participation metadata</li>
                  <li>Application usage patterns</li>
                  <li>AI agent interaction counts</li>
                  <li>Location metadata (with explicit consent)</li>
                </ul>
                <div className="bg-indigo-900/20 border border-indigo-400/20 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-indigo-300">Important:</p>
                  <p>Sensitive personal data (e.g., names, messages, payment information) will never be monetized.</p>
                  <p className="mt-2">All monetized data will be processed using zero-knowledge encryption standards (via PureCipher OmniSeal™ and Rubidex™ technologies).</p>
                </div>
              </div>
            </section>

            {/* Revenue Distribution */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Revenue and Reward Distribution</h2>
              <div className="prose prose-invert max-w-none">
                <p>Upon launch of Phase 2, the following distribution model will apply to all successful data monetization transactions:</p>
                <div className="overflow-x-auto mt-4">
                  <table className="min-w-full border border-indigo-400/20 rounded-lg">
                    <thead>
                      <tr className="bg-indigo-900/20">
                        <th className="px-6 py-3 text-left text-indigo-300">Participant</th>
                        <th className="px-6 py-3 text-left text-indigo-300">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-indigo-400/20">
                        <td className="px-6 py-4">You (the User)</td>
                        <td className="px-6 py-4">98%</td>
                      </tr>
                      <tr className="border-t border-indigo-400/20">
                        <td className="px-6 py-4">National Debt Reduction Program</td>
                        <td className="px-6 py-4">1%</td>
                      </tr>
                      <tr className="border-t border-indigo-400/20">
                        <td className="px-6 py-4">SELF Labs DAO (Governance/Operations)</td>
                        <td className="px-6 py-4">1%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* ESSENCE Payouts */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. ESSENCE Payouts</h2>
              <div className="prose prose-invert max-w-none">
                <div className="flex items-center gap-2 mb-4">
                  <Coins className="w-5 h-5 text-indigo-400" />
                  <p>All data monetization rewards will be paid in ESSENCE utility tokens.</p>
                </div>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payouts will be viewable and claimable directly through your SELF Phone dashboard.</li>
                  <li>SELF Labs makes no guarantee regarding the liquidity, price, or future tradability of ESSENCE.</li>
                </ul>
              </div>
            </section>

            {/* Timeline */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Timeline and Phase 2 Launch</h2>
              <div className="prose prose-invert max-w-none">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-indigo-300 mb-2">Phase 1:</h3>
                    <p>Focuses on hardware delivery, Mesh participation, agent execution, and the launch of the Role Model AI App Store.</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-indigo-300 mb-2">Phase 2:</h3>
                    <p>Activates full Aura Shield Data Monetization functionality, allowing users to:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Offer encrypted metadata streams</li>
                      <li>Manage monetization settings in real time</li>
                      <li>Earn ESSENCE from data buyers, AI training companies, researchers, and ethical data marketplaces</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4">A full announcement will be made once Phase 2 activation is ready, and users will receive onboarding guides.</p>
              </div>
            </section>

            {/* Control and Sovereignty */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Control and Sovereignty</h2>
              <div className="prose prose-invert max-w-none">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-indigo-400" />
                  <p>You retain full control at all times, including:</p>
                </div>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Choosing whether to opt-in or stay private</li>
                  <li>Selecting specific datasets to monetize</li>
                  <li>Locking, unlocking, or deleting data streams at any moment</li>
                  <li>Viewing transparent earnings history through your dashboard</li>
                </ul>
                <p className="mt-4">If you opt-out, no data will be listed, sold, or shared.</p>
              </div>
            </section>

            {/* No Sale of Raw Personal Data */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. No Sale of Raw Personal Data</h2>
              <div className="prose prose-invert max-w-none">
                <p>SELF Labs will never sell raw personal data.</p>
                <p>Only encrypted, anonymized metadata — generated from user consent — will be eligible for marketplace listing during Phase 2.</p>
              </div>
            </section>

            {/* Transparency */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Transparency and Tracking</h2>
              <div className="prose prose-invert max-w-none">
                <ul className="list-disc pl-6 space-y-2">
                  <li>All monetization actions will be logged in the SELF Ledger.</li>
                  <li>Users will have audit access to their transaction history via their SELF Phone.</li>
                </ul>
              </div>
            </section>

            {/* Termination */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">10. Termination and Updates</h2>
              <div className="prose prose-invert max-w-none">
                <p>SELF Labs reserves the right to modify this Data Monetization Policy in response to evolving platform needs, legal regulations, or market conditions.</p>
                <p>Users will be notified of material updates.</p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">11. Contact Information</h2>
              <div className="prose prose-invert max-w-none">
                <p>For all questions regarding Aura Shield Data Monetization, contact:</p>
                <div className="flex items-center space-x-2 mt-4">
                  <Mail className="w-5 h-5 text-indigo-400" />
                  <a href="mailto:data@rolemodel.ai" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    data@rolemodel.ai
                  </a>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Building className="w-5 h-5 text-indigo-400" />
                  <span>1209 N Orange St, Wilmington, Delaware</span>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DataMonetizationPolicyPage; 